<template>
  <div 
    class="waitlist-card"
    :class="{ 
      'urgent-today': isUrgentToday,
      'urgent-tomorrow': isUrgentTomorrow,
      'urgent-soon': isUrgentSoon,
      'compact': compact
    }"
  >
    <!-- Шапка: имя + телефон + время ожидания -->
    <div class="card-header">
      <div class="client-info">
        <span class="client-name">{{ entry.client_name || 'Без имени' }}</span>
        <span v-if="entry.client_phone" class="client-phone">{{ formatPhone(entry.client_phone) }}</span>
        <span class="wait-time" :class="waitTimeClass">{{ waitTimeText }}</span>
      </div>
      <div class="card-actions-compact">
        <!-- Для active заявок -->
        <template v-if="entry.status === 'active'">
          <button 
            @click="$emit('offer-slot', entry)" 
            class="btn-text btn-offer"
            title="Предложить клиенту свободный слот"
          >
            Предложить слот
          </button>
        </template>
        
        <!-- Для offered заявок -->
        <template v-if="entry.status === 'offered'">
          <button 
            v-if="canCancelOffer"
            @click="$emit('cancel-offer', entry.id)" 
            class="btn-icon btn-cancel"
            title="Отменить предложение"
            aria-label="Отменить предложение"
          >
            Отозвать
          </button>
          <button 
            v-if="canResendOffer"
            @click="$emit('offer-slot', entry)" 
            class="btn-text btn-offer"
            title="Повторить предложение"
          >
            Повторить
          </button>
        </template>
        
        <button 
          @click="$emit('delete-entry', entry.id)" 
          class="btn-icon btn-delete"
          title="Удалить заявку"
          aria-label="Удалить заявку"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6"/></svg>
        </button>
      </div>
    </div>

    <!-- Дата и время (главная информация) -->
    <div class="card-datetime" v-if="hasDateTime">
      <div class="datetime-main">
        <span class="date-badge" :class="dateBadgeClass">
          {{ formattedDate }}
        </span>
        <span v-if="formattedTime" class="time-range">
          {{ formattedTime }}
        </span>
      </div>
      <span v-if="entry.date_flexibility_days" class="flexibility-badge">
        ±{{ entry.date_flexibility_days }}д
      </span>
    </div>

    <!-- Услуги (теги) -->
    <div class="card-services" v-if="entry.services && entry.services.length">
      <span 
        v-for="svc in visibleServices" 
        :key="svc.id"
        class="service-tag"
        :style="getServiceStyle(svc)"
      >
        {{ svc.name }}
      </span>
      <span v-if="entry.services.length > 2" class="more-tag">
        +{{ entry.services.length - 2 }}
      </span>
    </div>

    <!-- Мастер (компактно) -->
    <div class="card-master" v-if="entry.preferred_master_name || entry.any_master_allowed">
      <span v-if="entry.preferred_master_name" class="master-name">
        {{ entry.preferred_master_name }}
      </span>
      <span v-else-if="entry.any_master_allowed" class="any-master">
        Любой мастер
      </span>
    </div>

    <!-- Длительность -->
    <div class="card-duration" v-if="totalDurationText">
      <span class="duration-label">{{ totalDurationText }}</span>
    </div>

    <!-- Счётчик предложений (rate limiting) -->
    <div v-if="entry.offer_count > 0" class="offer-counter" :class="{ 'rate-limited': isRateLimited }">
      Предложений: {{ entry.offer_count }}
      <span v-if="entry.max_offers">/ {{ entry.max_offers }}</span>
      <span v-if="entry.last_offered_at">· {{ lastOfferedText }}</span>
    </div>

    <!-- Блокировка слота (advisory lock) -->
    <div v-if="entry.slot_locked" class="slot-locked-badge" title="Слот временно забронирован за этой заявкой">
      Слот заблокирован
    </div>

    <!-- Предложенный слот (только для status === 'offered') -->
    <div v-if="entry.status === 'offered' && entry.offered_slot_date" class="offered-slot">
      <div class="offered-label">Предложено:</div>
      <div class="offered-details">
        <span class="offered-badge">{{ formatOfferedDate(entry.offered_slot_date) }}</span>
        <span class="offered-time">{{ entry.offered_slot_time }}</span>
        <span v-if="entry.offered_master_name" class="offered-master">
          {{ entry.offered_master_name }}
        </span>
      </div>
      
      <!-- Статус ответа клиента -->
      <div class="offer-status-row">
        <span v-if="tokenStatus === 'accepted'" class="status-badge accepted">Принято клиентом</span>
        <span v-else-if="tokenStatus === 'declined'" class="status-badge declined">Отклонено клиентом</span>
        <span v-else-if="tokenStatus === 'opened'" class="status-badge opened">Письмо открыто</span>
        <span v-else-if="isExpired" class="status-badge expired">Предложение истекло</span>
        <span v-else class="status-badge pending">Ожидает ответа</span>
        
        <!-- Tracking pixel: клиент открыл письмо -->
        <span v-if="entry.opened_at" class="tracking-badge" title="Клиент открыл письмо">
          {{ formatOpenedAt(entry.opened_at) }}
        </span>
      </div>
      
      <!-- Время до истечения -->
      <div v-if="entry.offer_expires_at" class="offer-expires" :class="isExpired ? 'expired' : 'active'">
        {{ expiresText }}
      </div>
    </div>

    <!-- Комментарий (если есть, свёрнут) -->
    <div class="card-notes" v-if="entry.notes && !compact">
      <span class="notes-text">{{ entry.notes }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  entry: { type: Object, required: true },
  compact: { type: Boolean, default: false }
})

const emit = defineEmits(['offer-slot', 'delete-entry', 'cancel-offer'])

// === БЕЗОПАСНОЕ ФОРМАТИРОВАНИЕ ДАТЫ (без времени) ===
function normalizeDate(dateInput) {
  if (!dateInput) return null
  if (dateInput instanceof Date) {
    const y = dateInput.getFullYear()
    const m = String(dateInput.getMonth() + 1).padStart(2, '0')
    const d = String(dateInput.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  if (typeof dateInput === 'string') {
    if (dateInput.includes('T')) {
      return dateInput.split('T')[0]
    }
    return dateInput
  }
  return String(dateInput)
}

function parseDate(dateStr) {
  const normalized = normalizeDate(dateStr)
  if (!normalized) return null
  const [year, month, day] = normalized.split('-').map(Number)
  if (!year || !month || !day || isNaN(year) || isNaN(month) || isNaN(day)) {
    return null
  }
  return new Date(year, month - 1, day)
}

// === ПОЛНЫЙ ПАРСИНГ ДАТЫ-ВРЕМЕНИ (с часами и минутами) ===
function parseDateTime(dateInput) {
  if (!dateInput) return null
  if (dateInput instanceof Date) return new Date(dateInput.getTime())
  if (typeof dateInput === 'string') {
    const d = new Date(dateInput)
    if (!isNaN(d.getTime())) return d
  }
  return null
}

// === ФОРМАТИРОВАНИЕ ===
const formattedDate = computed(() => {
  const date = parseDate(props.entry.preferred_date)
  if (!date) return 'Дата не указана'
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  
  const entryDate = new Date(date)
  entryDate.setHours(0, 0, 0, 0)
  
  if (entryDate.getTime() === today.getTime()) return 'Сегодня'
  if (entryDate.getTime() === tomorrow.getTime()) return 'Завтра'
  
  const diffDays = Math.round((entryDate - today) / (1000 * 60 * 60 * 24))
  if (diffDays === 2) return 'Послезавтра'
  if (diffDays > 2) return `Через ${diffDays} дн.`
  if (diffDays < 0) return `${Math.abs(diffDays)} дн. назад`
  
  const months = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек']
  return `${date.getDate()} ${months[date.getMonth()]}`
})

const formattedTime = computed(() => {
  const start = props.entry.preferred_time_start
  const end = props.entry.preferred_time_end
  if (!start) return ''
  const formatTime = (t) => t ? t.slice(0, 5) : ''
  if (end) return `${formatTime(start)}–${formatTime(end)}`
  return formatTime(start)
})

const hasDateTime = computed(() => {
  return props.entry.preferred_date || props.entry.preferred_time_start
})

// === СРОЧНОСТЬ ===
const isUrgentToday = computed(() => {
  const date = parseDate(props.entry.preferred_date)
  if (!date) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date.getTime() === today.getTime()
})

const isUrgentTomorrow = computed(() => {
  const date = parseDate(props.entry.preferred_date)
  if (!date) return false
  const tomorrow = new Date()
  tomorrow.setHours(0, 0, 0, 0)
  tomorrow.setDate(tomorrow.getDate() + 1)
  return date.getTime() === tomorrow.getTime()
})

const isUrgentSoon = computed(() => {
  const date = parseDate(props.entry.preferred_date)
  if (!date) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = Math.round((date - today) / (1000 * 60 * 60 * 24))
  return diff === 2
})

const dateBadgeClass = computed(() => {
  if (isUrgentToday.value) return 'badge-today'
  if (isUrgentTomorrow.value) return 'badge-tomorrow'
  if (isUrgentSoon.value) return 'badge-soon'
  return 'badge-normal'
})

// === ВРЕМЯ ОЖИДАНИЯ ===
const waitTimeText = computed(() => {
  const created = parseDateTime(props.entry.created_at)
  if (!created) return ''
  
  const now = new Date()
  const diffMs = now - created
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)
  
  if (diffDays > 0) return `В очереди ${diffDays}д`
  if (diffHours > 0) return `В очереди ${diffHours}ч`
  if (diffMinutes > 0) return `В очереди ${diffMinutes}мин`
  return 'Только что'
})

const waitTimeClass = computed(() => {
  const created = parseDateTime(props.entry.created_at)
  if (!created) return ''
  
  const now = new Date()
  const diffHours = Math.floor((now - created) / (1000 * 60 * 60))
  
  if (diffHours < 1) return 'fresh'
  if (diffHours < 24) return 'recent'
  if (diffHours < 72) return 'old'
  return 'stale'
})

// === УСЛУГИ ===
const visibleServices = computed(() => {
  return (props.entry.services || []).slice(0, 2)
})

const totalDurationText = computed(() => {
  const mins = props.entry.total_duration
  if (!mins || mins === 0) {
    const services = props.entry.services || []
    const total = services.reduce((sum, s) => sum + (s.duration_minutes || 0), 0)
    if (total === 0) return ''
    if (total >= 60) {
      const h = Math.floor(total / 60)
      const m = total % 60
      return m > 0 ? `${h}ч ${m}мин` : `${h}ч`
    }
    return `${total}мин`
  }
  if (mins >= 60) {
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return m > 0 ? `${h}ч ${m}мин` : `${h}ч`
  }
  return `${mins}мин`
})

function getServiceStyle(svc) {
  const color = svc.color || '#6366f1'
  return {
    background: color + '15',
    color: color,
    borderColor: color + '40'
  }
}

// === ТЕЛЕФОН КЛИЕНТА ===
function formatPhone(phone) {
  if (!phone) return ''
  let p = phone.replace(/\D/g, '')
  if (p.length === 11 && (p.startsWith('7') || p.startsWith('8'))) {
    p = '+7' + p.slice(1)
  }
  if (p.startsWith('+7') && p.length === 12) {
    return `${p.slice(0, 2)} (${p.slice(2, 5)}) ${p.slice(5, 8)}-${p.slice(8, 10)}-${p.slice(10, 12)}`
  }
  return phone
}

// === ТОКЕН / СТАТУС ПРЕДЛОЖЕНИЯ ===
const tokenStatus = computed(() => {
  return props.entry.token_status || 'pending'
})

const isExpired = computed(() => {
  if (!props.entry?.offer_expires_at) return false
  const expires = parseDateTime(props.entry.offer_expires_at)
  if (!expires) return false
  return expires < new Date()
})

const expiresText = computed(() => {
  const expires = parseDateTime(props.entry?.offer_expires_at)
  if (!expires) return ''
  const now = new Date()
  const diffMs = expires - now
  
  if (diffMs <= 0) return 'Предложение истекло'
  
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  
  if (diffHours > 0) return `До истечения: ${diffHours}ч ${diffMinutes}мин`
  return `До истечения: ${diffMinutes}мин`
})

const formatOfferedDate = (dateInput) => {
  if (!dateInput) return ''
  const date = parseDate(dateInput)
  if (!date) return String(dateInput)
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  
  const entryDate = new Date(date)
  entryDate.setHours(0, 0, 0, 0)
  
  if (entryDate.getTime() === today.getTime()) return 'Сегодня'
  if (entryDate.getTime() === tomorrow.getTime()) return 'Завтра'
  
  const months = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек']
  return `${date.getDate()} ${months[date.getMonth()]}`
}

// === TRACKING PIXEL ===
function formatOpenedAt(dateInput) {
  const date = parseDateTime(dateInput)
  if (!date) return ''
  const now = new Date()
  const diffMs = now - date
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  if (diffMinutes < 1) return 'только что'
  if (diffMinutes < 60) return `${diffMinutes} мин назад`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} ч назад`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays} дн назад`
}

// === RATE LIMITING ===
const isRateLimited = computed(() => {
  const max = props.entry.max_offers || 3
  return (props.entry.offer_count || 0) >= max
})

const lastOfferedText = computed(() => {
  const date = parseDateTime(props.entry.last_offered_at)
  if (!date) return ''
  const now = new Date()
  const diffMs = now - date
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  if (diffMinutes < 1) return 'только что'
  if (diffMinutes < 60) return `${diffMinutes} мин назад`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} ч назад`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays} дн назад`
})

// === УСЛОВИЯ ДЛЯ КНОПОК ===
const canCancelOffer = computed(() => {
  return props.entry.status === 'offered' && 
    (tokenStatus.value === 'pending' || tokenStatus.value === 'opened')
})

const canResendOffer = computed(() => {
  return props.entry.status === 'offered' && 
    (tokenStatus.value === 'declined' || tokenStatus.value === 'expired' || isExpired.value) &&
    !isRateLimited.value
})
</script>

<style scoped>
.waitlist-card {
  background: #fafafa;
  border-radius: 10px;
  padding: 0.75rem;
  border-left: 3px solid var(--brand-primary);
  transition: all 0.2s;
  cursor: default;
}

.waitlist-card:hover {
  background: #f3f4f6;
  transform: translateX(2px);
}

/* Срочность */
.waitlist-card.urgent-today {
  border-left-color: #ef4444;
  background: #fef2f2;
}

.waitlist-card.urgent-tomorrow {
  border-left-color: #f97316;
  background: #fff7ed;
}

.waitlist-card.urgent-soon {
  border-left-color: #eab308;
  background: #fefce8;
}

/* Компактный режим */
.waitlist-card.compact {
  padding: 0.5rem;
}

/* Шапка */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.client-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.client-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.client-phone {
  font-size: 0.75rem;
  color: #6b7280;
  font-family: 'SF Mono', monospace;
}

.wait-time {
  font-size: 0.7rem;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  width: fit-content;
}

.wait-time.fresh {
  background: #dcfce7;
  color: #166534;
}

.wait-time.recent {
  background: #dbeafe;
  color: #1e40af;
}

.wait-time.old {
  background: #fef3c7;
  color: #92400e;
}

.wait-time.stale {
  background: #f3f4f6;
  color: #6b7280;
}

/* Кнопки действий */
.card-actions-compact {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.btn-icon {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.btn-text {
  height: 28px; padding: 0 0.6rem; border: none; border-radius: 6px; cursor: pointer;
  font-size: 0.75rem; font-weight: 600; white-space: nowrap; transition: background 0.15s, color 0.15s;
}
.btn-offer {
  background: #dbeafe;
  color: #1e40af;
}

.btn-offer:hover {
  background: #3b82f6;
  color: white;
}

.btn-cancel {
  background: #fef3c7;
  color: #92400e;
}

.btn-cancel:hover {
  background: #f59e0b;
  color: white;
}

.btn-delete {
  background: #fee2e2;
  color: #991b1b;
}

.btn-delete:hover {
  background: #ef4444;
  color: white;
}

/* Дата и время */
.card-datetime {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
  flex-wrap: wrap;
}

.datetime-main {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.date-badge {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
}

.badge-today {
  background: #fecaca;
  color: #991b1b;
}

.badge-tomorrow {
  background: #fed7aa;
  color: #9a3412;
}

.badge-soon {
  background: #fde68a;
  color: #854d0e;
}

.badge-normal {
  background: #dbe5f1;
  color: #3730a3;
}

.time-range {
  font-size: 0.8rem;
  color: #6b7280;
  font-family: 'SF Mono', monospace;
}

.flexibility-badge {
  font-size: 0.7rem;
  color: var(--brand-primary-active);
  background: #f3e8ff;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  font-weight: 500;
}

/* Услуги */
.card-services {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.4rem;
}

.service-tag {
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  border: 1px solid;
  font-weight: 500;
}

.more-tag {
  font-size: 0.7rem;
  color: #9ca3af;
  padding: 0.15rem 0;
}

/* Мастер */
.card-master {
  font-size: 0.78rem;
  color: #6b7280;
  margin-bottom: 0.3rem;
}

.master-name {
  color: #4b5563;
}

.any-master {
  color: #9ca3af;
  font-style: italic;
}

/* Длительность */
.card-duration {
  font-size: 0.78rem;
  color: #4b5563;
  margin-bottom: 0.3rem;
  font-weight: 500;
}

.duration-label {
  background: #f3f4f6;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
}

/* Счётчик предложений */
.offer-counter {
  font-size: 0.7rem;
  color: #6b7280;
  margin-bottom: 0.3rem;
}

.offer-counter.rate-limited {
  color: #dc2626;
  font-weight: 600;
}

/* Блокировка слота */
.slot-locked-badge {
  font-size: 0.7rem;
  color: #7c3aed;
  background: #ede9fe;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  margin-bottom: 0.3rem;
  display: inline-block;
}

/* Предложенный слот */
.offered-slot {
  background: var(--brand-primary-soft);
  border-radius: 8px;
  padding: 0.6rem;
  margin-bottom: 0.4rem;
  border-left: 3px solid var(--brand-primary);
}

.offered-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--brand-primary-hover);
  margin-bottom: 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.offered-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
  margin-bottom: 0.3rem;
}

.offered-badge {
  background: var(--brand-primary);
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
}

.offered-time {
  font-family: 'SF Mono', monospace;
  font-size: 0.85rem;
  color: #1e293b;
  font-weight: 600;
}

.offered-master {
  font-size: 0.8rem;
  color: #4b5563;
}

/* Статусная строка */
.offer-status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-bottom: 0.3rem;
  align-items: center;
}

.status-badge {
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-weight: 500;
}

.status-badge.accepted { background: #dcfce7; color: #166534; }
.status-badge.declined { background: #fee2e2; color: #991b1b; }
.status-badge.opened { background: #dbeafe; color: #1e40af; }
.status-badge.expired { background: #f3f4f6; color: #6b7280; }
.status-badge.pending { background: #fef3c7; color: #92400e; }

.tracking-badge {
  font-size: 0.7rem;
  color: #4b5563;
  background: #f3f4f6;
  padding: 0.1rem 0.3rem;
  border-radius: 4px;
}

/* Время истечения */
.offer-expires {
  font-size: 0.75rem;
  font-weight: 500;
}

.offer-expires.active {
  color: #059669;
}

.offer-expires.expired {
  color: #dc2626;
}

/* Комментарий */
.card-notes {
  margin-top: 0.3rem;
  padding-top: 0.3rem;
  border-top: 1px dashed #e5e7eb;
}

.notes-text {
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>